import { Component, type ErrorInfo, type ReactNode } from "react";

interface NotificationBellBoundaryState {
  hasError: boolean;
}

/**
 * Renders nothing if the notification bell throws during render. The bell calls
 * `useOakStream` and `useFeatureFlags` which read from React contexts that aren't
 * always present (e.g. inside the top-level `<Oops />` error fallback). A failure
 * in the bell should never take down the surrounding chrome — `<TopNavBar>` and
 * the route's error UI must keep rendering so the user can recover.
 */
export class NotificationBellBoundary extends Component<
  { children: ReactNode },
  NotificationBellBoundaryState
> {
  state: NotificationBellBoundaryState = { hasError: false };

  static getDerivedStateFromError(): NotificationBellBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Stay quiet in production but surface during development so the underlying
    // problem is still visible; the boundary is a fallback, not a silencer.
    if (import.meta.env.DEV) {
      console.warn("[NotificationBellBoundary] suppressed bell error:", error, info);
    }
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}
